import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { updateRouteDataValues } from 'gain-web/lib/router/find-route-data';
import { findRouteParamFromRoot } from 'gain-web/lib/router/find-route-param';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { parse } from 'lossless-json';
import { CalculationContextCacheService } from './calculation-context-cache.service';
import {
  CalculationContext,
  findOrFetchCalculationContext,
} from './calculation-context.service';
export interface CalculationArgs {
  calculationId: string;
  clientPortalId: string;
}

export interface CacheObjectWithTimestamp<T> {
  timestamp: Date;
  object: T;
}

const calcCacheKeyPrefix = 'calculation:';

function getCalcCacheKeyRoot({
  clientPortalId,
  calculationId,
}: CalculationArgs) {
  return `${calcCacheKeyPrefix}clients/${clientPortalId}/calculations/${calculationId}`;
}

function getCalcIndexRecordCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/indexRecord.json`;
}

function getCalcPreProcessorInputsCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/preProcessorInputs.json`;
}

function getCalculationEngineInputsCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/calcEngineInputs.json`;
}

function getCalculationEngineFailureResultCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/calcEngineFailureResult.json`;
}

function getCalculationEngineResultsCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/calcEngineResults.json`;
}

export function getFullCalculationEngineResultsWithTimestampCacheKey(
  args: CalculationArgs,
) {
  return `${getCalcCacheKeyRoot(args)}/fullCalcEngineResultsWithTimestamp.json`;
}

function getCalculationEngineResultsWithTimestampCacheKey(
  args: CalculationArgs,
) {
  return `${getCalcCacheKeyRoot(args)}/calcEngineResultsWithTimestamp.json`;
}

function getCalculationEngineOriginalResultsCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/calcEngineOriginalResults.json`;
}

function getCalculationEngineLatestRunCacheKey(args: CalculationArgs) {
  return `${getCalcCacheKeyRoot(args)}/calcEngineLatestRun.json`;
}

@Injectable({ providedIn: 'root' })
export class CalculationContextResolver implements Resolve<CalculationContext> {
  constructor(
    private _router: Router,
    private calculationContextService: ApiClient.CalculationContextService,
    private cacheService: CalculationContextCacheService,
  ) {}

  async resolve(route: ActivatedRouteSnapshot) {
    const calculationId = route.paramMap.get('calculationId');
    if (calculationId == null) {
      throw new Error('Cannot find calculation ID in param map');
    }
    const clientPortalId = findRouteParamFromRoot(route, 'portalId');
    const args = { calculationId, clientPortalId };

    await this.trimIndexedDb(args);

    try {
      const calculationContext = await findOrFetchCalculationContext(
        route,
        this.calculationContextService,
      );

      updateRouteDataValues(route, 'calculationContext', calculationContext);

      let indexRecord = await this.getCalcIndexRecord(args);
      let preProcessorInputs: ApiClient.PreProcessorInputs | null = null;
      if (indexRecord == null || !indexRecord.finished) {
        // eslint-disable-next-line no-console
        console.debug(
          `Fetching calculation because it's not in the cache or hasn't finished processing. `,
        );
        const response = await this.calculationContextService
          .getCalculationAndPreProcessorInputs(
            args.clientPortalId,
            args.calculationId,
          )
          .toPromise();
        preProcessorInputs = response.preProcessorInputs;
        localStorage.setItem(
          getCalcIndexRecordCacheKey(args),
          JSON.stringify(response.indexModel.toJSON()),
        );
        localStorage.setItem(
          getCalcPreProcessorInputsCacheKey(args),
          JSON.stringify(preProcessorInputs.toJSON()),
        );
        indexRecord = response.indexModel;
      }
      preProcessorInputs =
        preProcessorInputs ?? (await this.getPreProcessorInputs(args));
      const calcEngineInputs =
        indexRecord.timePreProcessingInMs != null
          ? await this.getCalcEngineInputs(args)
          : null;
      const calcEngineResults = indexRecord.finishedOrInPostProcessing
        ? await this.getFullCalcEngineResultsWithTimestamp(args, indexRecord)
        : null;

      const calcEngineFailureResult =
        indexRecord.timeCalculatingWithFailureInMs != null
          ? await this.getCalcEngineFailureResult(args)
          : null;

      const result = new CalculationContext(
        new ApiClient.CalculationContextDto({
          calculationId,
          batchId: indexRecord.batchId,
          clientPortalId: indexRecord.clientPortalId,
          indexRecord,
          preProcessorInputs,
          calcEngineInputs,
          calcEngineResults: calcEngineResults?.object?.employeeResults ?? null,
          employerCalcEngineResults:
            calcEngineResults?.object?.employerResults ?? null,
          calcEngineFailureResult,
          hasTeqConsideredWorkLocations:
            calculationContext.hasTeqConsideredWorkLocations,
          calculationCanBeClonedToOriginalBatch:
            calculationContext.calculationCanBeClonedToOriginalBatch,
        }),

        this.calculationContextService,

        async () => {
          return indexRecord.finishedOrInPostProcessing
            ? await this.getCalcEngineOriginalResults(args)
            : null;
        },
        async () => {
          return indexRecord.timePreProcessingInMs != null
            ? await this.getLatestFormattedInputsAndResults(args, indexRecord)
            : null;
        },
      );

      return result;
    } catch (err: any) {
      const unauthorized = err.status === 401 || err.status === 403;
      const notFound = err.status === 404;
      const redirectUrl = notFound
        ? '/not-found'
        : unauthorized
          ? '/unauthorized'
          : null;

      if (redirectUrl != null) {
        await this._router.navigateByUrl(redirectUrl, {
          skipLocationChange: true,
          replaceUrl: true,
        });
      }

      throw err;
    }
  }

  private async trimIndexedDb(args: CalculationArgs) {
    const currentCalcKeys = [
      getCalculationEngineResultsWithTimestampCacheKey(args),
      getCalcIndexRecordCacheKey(args),
      getCalcPreProcessorInputsCacheKey(args),
      getCalculationEngineInputsCacheKey(args),
      getCalculationEngineFailureResultCacheKey(args),
      getCalculationEngineLatestRunCacheKey(args),
      getFullCalculationEngineResultsWithTimestampCacheKey(args),
    ];
    const legacyKeySegments = [
      'calcEngineResults.json',
      'calcEngineFailureResult.json',
      'calcEngineInputs.json',
      'preProcessorInputs.json',
      'calcEngineFailureResult.json',
      'calcEngineRawResults',
      getCalculationEngineResultsCacheKey(args),
    ];
    const keys = await this.cacheService.keys();
    for (const key of keys) {
      const isCurrentCalcKey = currentCalcKeys.includes(key);
      const isCalcKey = key.indexOf(calcCacheKeyPrefix) === 0;
      const isLegacyCalcKey =
        !isCurrentCalcKey &&
        legacyKeySegments.find((s) => key.includes(s)) != null;
      if (!isCurrentCalcKey && (isCalcKey || isLegacyCalcKey)) {
        // eslint-disable-next-line no-console
        console.debug('trimming indexed db', key);
        await this.cacheService.remove(key);
      }
    }
  }

  private async getOrElse<T>(
    key: string,
    onCacheHit: (cachedValue: string) => T,
    onCacheMiss: () => Promise<{ returnValue: T; valueToCache?: unknown }>,
    shouldRefetch: (result: T) => boolean = () => false,
  ) {
    const cached = await this.cacheService.get<string>(key);
    if (cached != null) {
      // eslint-disable-next-line no-console
      console.debug(`cache hit: ${key}`);
      const returnValue = onCacheHit(cached);

      if (!shouldRefetch(returnValue)) {
        return returnValue;
      }
    }

    // eslint-disable-next-line no-console
    console.debug(`cache miss: ${key}`);
    const { returnValue, valueToCache } = await onCacheMiss();
    if (valueToCache != null) {
      try {
        await this.cacheService.set(key, JSON.stringify(valueToCache));
      } catch (e) {
        console.error('Error caching value', e);
      }
    }
    return returnValue;
  }

  private async getCalcIndexRecord(args: CalculationArgs) {
    return this.getOrElse(
      getCalcIndexRecordCacheKey(args),
      (json) => ApiClient.CalculationIndexModel.fromJS(parse(json)),
      async () => {
        const response = await this.calculationContextService
          .getCalculationAndPreProcessorInputs(
            args.clientPortalId,
            args.calculationId,
          )
          .toPromise();
        await this.cacheService.set(
          getCalcPreProcessorInputsCacheKey(args),
          JSON.stringify(response.preProcessorInputs.toJSON()),
        );
        return {
          valueToCache: response.indexModel.toJSON(),
          returnValue: response.indexModel,
        };
      },
    );
  }

  private async getPreProcessorInputs(args: CalculationArgs) {
    return this.getOrElse(
      getCalcPreProcessorInputsCacheKey(args),
      (json) => ApiClient.PreProcessorInputs.fromJS(JSON.parse(json)),
      async () => {
        const response = await this.calculationContextService
          .getCalculationAndPreProcessorInputs(
            args.clientPortalId,
            args.calculationId,
          )
          .toPromise();
        return {
          valueToCache: response.preProcessorInputs.toJSON(),
          returnValue: response.preProcessorInputs,
        };
      },
    );
  }

  private async getFullCalcEngineResultsWithTimestamp(
    args: CalculationArgs,
    indexRecord: ApiClient.CalculationIndexModel,
  ) {
    return this.getOrElse(
      getFullCalculationEngineResultsWithTimestampCacheKey(args),
      (json) => {
        const objectWithTimestamp: CacheObjectWithTimestamp<any> =
          JSON.parse(json);

        return {
          object: ApiClient.CalculationEngineResults.fromJS(
            objectWithTimestamp.object,
          ),
          timestamp: new Date(objectWithTimestamp.timestamp),
        };
      },
      async () => {
        const response = await this.calculationContextService
          .getCalculationEngineResults(args.clientPortalId, args.calculationId)
          .toPromise();

        const calculationEngineResults = response.calculationEngineResults;
        const result: CacheObjectWithTimestamp<ApiClient.ICalculationEngineResults | null> =
          {
            timestamp: new Date(),
            object: calculationEngineResults,
          };

        return {
          valueToCache: {
            timestamp: result.timestamp,
            object: calculationEngineResults,
          },
          returnValue: result,
        };
      },
      (res) => {
        if (!res?.timestamp) {
          return false;
        }

        const timestamp =
          indexRecord.latestBrokerAdjustmentJob?.finishedTimestamp ??
          indexRecord.clientValidationEditTimestamp ??
          indexRecord.manualCalculationEditTimestamp ??
          indexRecord.calculationCompletedTimestamp;

        return !!(timestamp && res.timestamp < timestamp);
      },
    );
  }

  private async getLatestFormattedInputsAndResults(
    args: CalculationArgs,
    indexRecord: ApiClient.CalculationIndexModel,
  ) {
    return this.getOrElse(
      getCalculationEngineLatestRunCacheKey(args),
      (json) =>
        ApiClient.CalcEngineLatestFormattedInputsAndResults.fromJS(parse(json)),
      async () => {
        try {
          const response = await this.calculationContextService
            .getLatestFormattedInputsAndResults(
              args.clientPortalId,
              args.calculationId,
            )
            .toPromise();
          return {
            valueToCache: response.result?.toJSON(),
            returnValue: response.result,
          };
        } catch (e) {
          console.error(e);
          return {
            valueToCache: null,
            returnValue: null,
          };
        }
      },
      (res) => {
        const timestamp =
          indexRecord.latestBrokerAdjustmentJob?.finishedTimestamp ??
          indexRecord.latestGainAdjustmentJob?.finishedTimestamp ??
          indexRecord.clientValidationEditTimestamp ??
          indexRecord.manualCalculationEditTimestamp ??
          indexRecord.calculationCompletedTimestamp ??
          indexRecord.calculationFailedTimestamp;

        return !!(timestamp && (!res?.timestamp || res.timestamp < timestamp));
      },
    );
  }

  private async getCalcEngineOriginalResults(args: CalculationArgs) {
    return this.getOrElse(
      getCalculationEngineOriginalResultsCacheKey(args),
      (json) => ApiClient.CalculationEngineResults.fromJS(parse(json)),
      async () => {
        const response = await this.calculationContextService
          .getCalculationEngineOriginalResults(
            args.clientPortalId,
            args.calculationId,
          )
          .toPromise();

        const calculationEngineOriginalResults =
          response.calculationEngineResults;

        return {
          valueToCache: calculationEngineOriginalResults,
          returnValue: calculationEngineOriginalResults,
        };
      },
    );
  }

  private async getCalcEngineInputs(args: CalculationArgs) {
    return this.getOrElse(
      getCalculationEngineInputsCacheKey(args),
      (json) => ApiClient.CalculationEngineInputs.fromJS(parse(json)),
      async () => {
        const response = await this.calculationContextService
          .getCalculationEngineInputs(args.clientPortalId, args.calculationId)
          .toPromise();
        return {
          valueToCache: response.calculationEngineInputs?.toJSON(),
          returnValue: response.calculationEngineInputs,
        };
      },
    );
  }

  private async getCalcEngineFailureResult(args: CalculationArgs) {
    return this.getOrElse(
      getCalculationEngineFailureResultCacheKey(args),
      (json) =>
        ApiClient.CalculateEmployeeCommandResultFailure.fromJS(
          JSON.parse(json),
        ),
      async () => {
        const response = await this.calculationContextService
          .getCalculationEngineFailureResult(
            args.clientPortalId,
            args.calculationId,
          )
          .toPromise();
        return {
          valueToCache: response.failure?.toJSON(),
          returnValue: response.failure,
        };
      },
    );
  }
}
